import Moment from "react-moment";
import { useContext, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Printer } from "lucide-react";
import logo from "../../../assets/images/blue_logo.png";
import { Link } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import { AuthContext } from "../../../contexts/AuthContext";

const adminAccess = [9];

export default function FormRead({ request }: any) {
  const { user } = useContext(AuthContext);
  const printRef = useRef<HTMLDivElement>(null);
  const attachments = request?.attachments?.length ? request.attachments : request?.thumbnail ? [request.thumbnail] : [];
  const isImageAttachment = (url: string) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
  });

  const canEdit =
    request?.approvalStatus === "Pending" &&
    (request?.requestedBy?._id === user?._id || adminAccess.includes(user?.accessLevel));

  return (
    <div className="">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }

          .print-form-grid {
            display: flex !important;
            flex-wrap: wrap !important;
          }
          .print-form-half {
            flex: 0 0 50% !important;
            max-width: 50% !important;
            padding: 0 4px !important;
            box-sizing: border-box !important;
          }
          .print-form-full {
            flex: 0 0 100% !important;
            max-width: 100% !important;
            padding: 0 4px !important;
            box-sizing: border-box !important;
          }
          .print-field {
            margin-bottom: 6px !important;
          }
          .print-field-label {
            font-size: 11px !important;
            font-weight: 600 !important;
            display: block !important;
            margin-bottom: 2px !important;
            color: #000 !important;
          }
          .print-field-value {
            font-size: 12px !important;
            border: 1px solid #999 !important;
            min-height: 28px !important;
            padding: 3px 6px !important;
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
            color: #000 !important;
          }
          .print-field-value-tall {
            min-height: 48px !important;
          }
          .print-section-title {
            font-size: 11px !important;
            font-weight: 700 !important;
            border-bottom: 1px solid #000 !important;
            padding-bottom: 3px !important;
            margin-bottom: 6px !important;
            margin-top: 10px !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
          }
          .print-header img {
            width: 80px !important;
          }
          .print-header-title {
            font-size: 16px !important;
            font-weight: 700 !important;
            text-align: center !important;
            margin: 4px 0 12px !important;
          }
          .approval-section {
            margin-top: 24px !important;
            border-top: 1px solid #000 !important;
            padding-top: 12px !important;
          }
          .approval-grid {
            display: flex !important;
            gap: 24px !important;
          }
          .approval-item {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
          }
          .approval-label {
            font-size: 11px !important;
            font-weight: 600 !important;
            color: #333 !important;
          }
          .approval-line {
            border-bottom: 1px solid #000 !important;
            height: 32px !important;
            width: 100% !important;
          }
        }

        .print-only { display: none; }
      `}</style>

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row justify-between border p-2 m-1 bg-white">
        <div className="">
          <div className="font-bold capitalize text-lg">
            Requested By: {request?.requestedBy?.name}
          </div>
          <div className="text-gray-600">
            Business Unit: {request?.businessUnit?.name}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 sm:mt-0">
          {canEdit && (
            <Link
              to={`/change/requests/${request?._id}/edit`}
              className="flex border bg-yellow-100 hover:bg-yellow-200 hover:border-yellow-600 justify-center items-center px-3 py-1 text-sm gap-x-2"
            >
              <EditOutlined /> Edit
            </Link>
          )}
          <button
            type="button"
            onClick={() => reactToPrintFn()}
            className="flex border bg-blue-100 hover:bg-blue-200 hover:border-blue-600 justify-center items-center px-3 py-1 text-sm gap-x-2"
          >
            <Printer size={16} color="#424242" /> Print Form
          </button>
        </div>
      </div>

      {/* Visible read view */}
      <div className="m-1 no-print">
        <div className="mx-auto bg-white shadow-sm p-8">
          <div className="grid grid-cols-12 gap-x-8">
            <div className="col-span-12 sm:col-span-8 leading-relaxed space-y-4">
              <div>
                <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                  General Information
                </h2>
                <p><span className="font-semibold">Project:</span> {request?.projectName || "-"}</p>
                <p><span className="font-semibold">Change No.:</span> 1011</p>
                <p>
                  <span className="font-semibold">Date Requested:</span>{" "}
                  {request?.dateRequested || request?.createdAt ? (
                    <Moment format="DD/MM/YYYY">{request?.dateRequested || request?.createdAt}</Moment>
                  ) : "-"}
                </p>
                <p><span className="font-semibold">Requested By:</span> {request?.requestedBy?.name || "-"}</p>
                <p><span className="font-semibold">Contact Email:</span> {request?.contactEmail || "-"}</p>
                <p><span className="font-semibold">Business Unit:</span> {request?.businessUnit?.name || "-"}</p>
                <p><span className="font-semibold">Year:</span> {request?.year ?? "-"}</p>
              </div>

              <div>
                <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                  Change Details
                </h2>
                <p><span className="font-semibold">Description:</span> {request?.description || "-"}</p>
                <p><span className="font-semibold">Reason:</span> {request?.reason || "-"}</p>
                <p><span className="font-semibold">Scope:</span> {request?.scope || "-"}</p>
                <p><span className="font-semibold">Deliverables:</span> {request?.deliverables || "-"}</p>
                <p><span className="font-semibold">Priority:</span> {request?.priority || "-"}</p>
                <p><span className="font-semibold">Resources:</span> {request?.resources || "-"}</p>
                <p><span className="font-semibold">Timeline (weeks):</span> {request?.timeline ?? "-"}</p>
                <p><span className="font-semibold">Estimated Cost:</span> {request?.cost || "-"}</p>
              </div>

              <div>
                <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                  Stakeholders & Risk
                </h2>
                <p><span className="font-semibold">Stakeholders:</span> {request?.stakeholders || "-"}</p>
                <p><span className="font-semibold">Risk Identification:</span> {request?.riskIdentification || "-"}</p>
                <p><span className="font-semibold">Risk Mitigation:</span> {request?.riskMitigation || "-"}</p>
              </div>

              <div>
                <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                  Decision & Approval
                </h2>
                <p><span className="font-semibold">Status:</span> {request?.status || "Pending"}</p>
                <p><span className="font-semibold">Approval Status:</span> {request?.approvalStatus || "Pending"}</p>
                <p><span className="font-semibold">Approved By:</span> {request?.approvedBy?.name || "-"}</p>
                <p>
                  <span className="font-semibold">Date Approved:</span>{" "}
                  {request?.dateApproved ? (
                    <Moment format="DD/MM/YYYY">{request?.dateApproved || request?.updatedAt}</Moment>
                  ) : "-"}
                </p>
              </div>
            </div>

            <div className="col-span-12 sm:col-span-4 mt-6 sm:mt-0">
              {request?.description && (
                <div className="mb-6">
                  <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                    Summary of Change
                  </h2>
                  <p className="text-gray-700 text-sm">{request?.description}</p>
                </div>
              )}
              {attachments?.length > 0 && (
                <div>
                  <h2 className="text-md font-bold text-karimjee border-b-2 border-gray-300 pb-2 mb-2">
                    Attachment{attachments.length > 1 ? "s" : ""}
                  </h2>
                  <div className="space-y-2">
                    {attachments.map((attachment: string, index: number) => (
                      <a
                        key={`${attachment}-${index}`}
                        href={attachment}
                        target="_blank"
                        rel="noreferrer"
                        download={`Document-${index + 1}`}
                        className="border border-gray-300 px-2 py-2 flex justify-center items-center bg-gray-50"
                      >
                        {isImageAttachment(attachment) ? (
                          <img
                            src={attachment}
                            alt="Change request attachment"
                            className="max-h-40 object-contain"
                          />
                        ) : (
                          <span className="text-sm text-blue-600 underline">Document {index + 1}</span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print form — only visible when printing */}
      <div className="print-only" ref={printRef as any} style={{ padding: "50px" }}>

        {/* Header */}
        <div className="print-header" style={{ textAlign: "center", marginBottom: "12px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "6px" }}>
            <img src={logo} alt="logo" className="w-56" />
          </div>
          <div className="print-header-title">Change Request Form</div>
        </div>

        {/* Row 1: Project Name & Business Unit */}
        <div className="print-form-grid">
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Project Name</span>
              <span className="print-field-value">{request?.projectName || ""}</span>
            </div>
          </div>
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Business Unit</span>
              <span className="print-field-value">{request?.businessUnit?.name || ""}</span>
            </div>
          </div>
        </div>

        {/* Row 2: Requested By & Contact — intentionally blank for print */}
        <div className="print-form-grid">
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Requested By</span>
              <span className="print-field-value">{request?.requestedBy?.name || ""}</span>
            </div>
          </div>
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Contact Information</span>
              <span className="print-field-value">{request?.contactEmail || ""}</span>
            </div>
          </div>
        </div>

        {/* Row 3: Priority — intentionally blank for print */}
        <div className="print-form-grid">
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Priority</span>
              <span className="print-field-value">{request?.priority || ""}</span>
            </div>
          </div>
        </div>

        {/* Change Details */}
        <div className="print-section-title">Change Details</div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Description of Change</span>
              <span className={`print-field-value print-field-value-tall`}>{request?.description || ""}</span>
            </div>
          </div>
        </div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Reason for Change</span>
              <span className="print-field-value print-field-value-tall">{request?.reason || ""}</span>
            </div>
          </div>
        </div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Scope</span>
              <span className="print-field-value print-field-value-tall">{request?.scope || ""}</span>
            </div>
          </div>
        </div>

        {/* Timeline & Deliverables */}
        <div className="print-form-grid">
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Timeline</span>
              <span className="print-field-value">{request?.timeline || ""}</span>
            </div>
          </div>
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Deliverables</span>
              <span className="print-field-value">{request?.deliverables || ""}</span>
            </div>
          </div>
        </div>

        {/* Cost & Resources */}
        <div className="print-form-grid">
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Cost</span>
              <span className="print-field-value">{request?.cost || ""}</span>
            </div>
          </div>
          <div className="print-form-half">
            <div className="print-field">
              <span className="print-field-label">Resources</span>
              <span className="print-field-value">{request?.resources || ""}</span>
            </div>
          </div>
        </div>

        {/* Stakeholders & Risk */}
        <div className="print-section-title">Stakeholders & Risk</div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Stakeholders</span>
              <span className="print-field-value print-field-value-tall">{request?.stakeholders || ""}</span>
            </div>
          </div>
        </div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Risk Identification</span>
              <span className="print-field-value print-field-value-tall">{request?.riskIdentification || ""}</span>
            </div>
          </div>
        </div>

        <div className="print-form-grid">
          <div className="print-form-full">
            <div className="print-field">
              <span className="print-field-label">Risk Mitigation Strategies</span>
              <span className="print-field-value print-field-value-tall">{request?.riskMitigation || ""}</span>
            </div>
          </div>
        </div>

        {/* Approval */}
        <div className="approval-section">
          <div className="approval-grid">
            <div className="approval-item">
              <span className="approval-label">Approved By</span>
              <div className="approval-line text-xs">{request?.approvedBy?.name || ""}</div>
            </div>
            <div className="approval-item">
              <span className="approval-label">Date</span>
              <div className="approval-line text-xs">{request?.dateApproved ? (
                <Moment format="DD/MM/YYYY">{request?.dateApproved || request?.updatedAt}</Moment>
              ) : "-"}</div>
            </div>
            <div className="approval-item">
              <span className="approval-label">Signature</span>
              <div className="approval-line" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}